import { yupResolver } from "@hookform/resolvers/yup";
import RightArrowIcon from "@mui/icons-material/KeyboardBackspaceOutlined";
import { Container } from "@mui/material";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { getFormattedUrl } from "../../common/utils";
import CButton from "../../components/CButton";
import CFormProvider from "../../components/CFormProvider";
import CFormTextField from "../../components/CFormTextField";
import CImageUpload from "../../components/CImageUpload";
import CustomBreadcrumbs from "../../components/breadcrumbs/CustomBreadcrumbs";
import {
  fetchProfileDetails,
  removeProfileImage,
  uploadProfileImage,
} from "../../slices/profileSlice";
import SettingSidebar from "./SettingSidebar";

const Profile = ({ profileImgUrl, ...props }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { profileDetails } = useSelector((state) => state.profile);
  const { userDetails } = useSelector((state) => state.auth);

  // Check if user was redirected here because they need a profile picture
  const requireProfilePicture = location.state?.requireProfilePicture;

  const ProfileSchema = Yup.object().shape({
    firstName: Yup.string().required("First Name is required"),
    lastName: Yup.string().required("Last Name is required"),
    phone: Yup.string().required("Phone is required"),
    lmaContactNo: Yup.string().required("LMA Contact is required"),
    employerName: Yup.string().required("Employer Name is required"),
    employeeNumber: Yup.string().required("Employee Number is required"),
  });

  const [defaultValues, setDefaultValues] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    lmaContactNo: "",
    employerName: "",
    employeeNumber: "",
    email: "",
  });

  const methods = useForm({
    resolver: yupResolver(ProfileSchema),
    defaultValues,
  });

  const {
    handleSubmit,
    // formState: { isSubmitting },
  } = methods;

  useEffect(() => {
    profileDetails !== null && setDefaultValues(profileDetails);
  }, [profileDetails]);

  useEffect(() => {
    methods.reset(defaultValues);
  }, [defaultValues, methods]);

  const handleContractDownload = (event) => {
    event.stopPropagation();
    if (profileDetails?.contractDocument) {
      const url =
        profileDetails.contractDocument.startsWith("http") ||
        profileDetails.contractDocument.startsWith("https")
          ? profileDetails.contractDocument
          : `http://${profileDetails.contractDocument}`;

      fetch(url)
        .then((response) => response.blob())
        .then((blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "contract.pdf");
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .catch((error) => {
          console.error("Error downloading contract:", error);
        });
    }
  };

  const handleProfileChange = (image) => {
    if (image.length) {
      const file = image[0].file;
      const fileType = file.type.split("/")[0];
      const fileSize = file.size / 1024 / 1024; // size in MB

      if (fileType !== "image") {
        toast.error("The uploaded file must be an image.");
        return;
      }

      if (fileSize > 3) {
        toast.error("The uploaded image must be less than 3 MB.");
        return;
      }

      let formDataPayload = new FormData();
      formDataPayload.append("image", file);
      dispatch(uploadProfileImage(formDataPayload)).then((res) => {
        if (!res.error) {
          localStorage.setItem(
            "profileImg",
            getFormattedUrl(res.payload.data.profile)
          );
          sessionStorage.setItem(
            "profileImg",
            getFormattedUrl(res.payload.data.profile)
          );

          // Update the user object in storage with the new profile information
          const updatedUser = { ...userDetails, profile: res.payload.data.profile };
          localStorage.setItem('user', JSON.stringify(updatedUser));
          sessionStorage.setItem('user', JSON.stringify(updatedUser));

          // Dispatch custom event to notify components of user data change
          window.dispatchEvent(new CustomEvent('userDataUpdated'));

          dispatch(fetchProfileDetails());

          // If user was redirected here for profile picture requirement, navigate appropriately
          if (requireProfilePicture) {
            toast.success("Profile picture uploaded successfully! Redirecting to dashboard...");
            
            // Redirect to dashboard after successful profile picture upload
            setTimeout(() => {
              navigate('/module');
            }, 1500); // Small delay to show the success message
          }
        }
      });
    } else {
      dispatch(removeProfileImage()).then((res) => {
        if (!res.error) {
          localStorage.setItem("profileImg", null);
          sessionStorage.setItem("profileImg", null);

          // Update the user object in storage to remove the profile information
          const updatedUser = { ...userDetails, profile: null };
          localStorage.setItem('user', JSON.stringify(updatedUser));
          sessionStorage.setItem('user', JSON.stringify(updatedUser));

          // Dispatch custom event to notify components of user data change
          window.dispatchEvent(new CustomEvent('userDataUpdated'));

          dispatch(fetchProfileDetails());
        }
      });
    }
  };

  const handleBackNavigation = () => {
    if (requireProfilePicture && !profileDetails?.profile) {
      toast.warning("Please upload a profile picture before continuing.");
      return;
    }
    navigate("/module");
  };

  useEffect(() => {
    dispatch(fetchProfileDetails());
  }, [dispatch]);

  return (
    <>
      <div className="main-content">
        <Container fixed className="setting-container">
          <div className="breadcrumb-back">
            <CButton
              sx={{ border: " 0 !important" }}
              onClick={handleBackNavigation}
            >
              <RightArrowIcon />
            </CButton>
            <CustomBreadcrumbs
              links={[
                {
                  name: "Dashboard",
                  href: "/module",
                },
                {
                  name: "My Profile",
                  href: "",
                },
              ]}
            />
          </div>
          <div className="setting-content">
            <div className="card-box col-left">
              <SettingSidebar />
            </div>
            <div className="card-box col-right">
              {requireProfilePicture && (
                <div className="profile-requirement-notice" style={{
                  backgroundColor: '#fff3cd',
                  border: '1px solid #ffc107',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '20px',
                  color: '#856404'
                }}>
                  <h4 style={{ margin: '0 0 8px 0', fontWeight: '600' }}>
                    Profile Picture Required
                  </h4>
                  <p style={{ margin: '0', fontSize: '14px' }}>
                    Please upload a profile picture to continue using the application.
                    This helps us verify your identity and personalize your experience.
                  </p>
                </div>
              )}
              <div className="profile-content">
                <div className="profile-upload">
                  <CImageUpload
                    onChange={handleProfileChange}
                    defaultImage={profileDetails?.profile}
                    profileDetails={profileDetails}
                  />
                  <div className="profile-title-text">
                    {profileDetails && (
                      <h3>
                        {profileDetails?.firstName +
                          " " +
                          profileDetails?.lastName}
                      </h3>
                    )}
                    {/* <span className="profile-email-text">
                      johan.mark@testemail.com
                    </span> */}
                  </div>
                </div>
                {/* <CButton className="btn-primary edit-profile-btn">
                  Edit Profile
                </CButton> */}
              </div>
              <div className="edit-profile-form">
                <CFormProvider methods={methods} onSubmit={handleSubmit()}>
                  <CFormTextField
                    label="First Name"
                    name="firstName"
                    // placeholder="John"
                    // value="John"
                    disabled={true}
                    InputLabelProps={{ shrink: true }}
                  />
                  <CFormTextField
                    label="Last Name"
                    name="lastName"
                    // placeholder="Doe"
                    // value="Doe"
                    disabled={true}
                    InputLabelProps={{ shrink: true }}
                  />
                  <CFormTextField
                    label="Employer Name"
                    name="employerName"
                    // placeholder="1234567890"
                    // value="1234567890"
                    disabled={true}
                    InputLabelProps={{ shrink: true }}
                  />
                  <CFormTextField
                    label="Email"
                    name="email"
                    // placeholder="1234567890"
                    // value="1234567890"
                    disabled={true}
                    InputLabelProps={{ shrink: true }}
                  />
                  {/* <CFormTextField
                    label="Phone Number"
                    name="phone"
                    // placeholder="1234567890"
                    // value="1234567890"
                    disabled={true}
                    InputLabelProps={{ shrink: true }}
                  /> */}
                  <CFormTextField
                    label="Employee Number"
                    name="employeeNumber"
                    // placeholder="1234567890"
                    // value="1234567890"
                    disabled={true}
                    InputLabelProps={{ shrink: true }}
                  />
                  <CFormTextField
                    label="My Contact Number"
                    name="lmaContactNo"
                    // placeholder="1234567890"
                    // value="1234567890"
                    disabled={true}
                    InputLabelProps={{ shrink: true }}
                  />
                  {/* <CFormTextField
                    label="Email ID"
                    name="emailID"
                    placeholder="johndoe@gmail.com"
                    value="johndoe@gmail.com"
                    InputLabelProps={{ shrink: true }}
                  /> */}
                </CFormProvider>
              </div>
            </div>
          </div>
        </Container>
      </div>
    </>
  );
};

export default Profile;
